import { ensureIndexedDbSpace } from "@Services/utils/modelPropertiesDataDB";

const DB_NAME = "nodeIdAndUniqueIdMapData";
const STORE = "nodeIdAndUniqueIdMap";
const DB_VERSION = 1;

export interface NodeIdUniqueIdFileEntry {
  /** Maps nodeId (string) → cubeUniqueId. Values are strings at runtime. */
  nodeIdMap: Record<string, string | number | boolean | null> | null;
  /** Inverse: cubeUniqueId (string) → nodeIds (number[]). */
  cubeUniqueIdMap: Record<string, number[]>;
}

type StoredRow = { fileVersionId: string } & NodeIdUniqueIdFileEntry;

const memoryCache = new Map<string, NodeIdUniqueIdFileEntry>();
let dbInstance: IDBDatabase | null = null;
let openPromise: Promise<IDBDatabase> | null = null;

/** Drop the cached handle so the next `openDB()` reconnects instead of reusing a corpse. */
function forget(db: IDBDatabase): void {
  if (dbInstance === db) dbInstance = null;
}

/**
 * True while `db` can still start transactions. The browser retires connections on
 * its own — another tab's versionchange, storage eviction, a backing-store failure —
 * and exposes no flag to read, so the only way to ask is to start a throwaway
 * transaction: it throws InvalidStateError once the connection is closing. An unused
 * transaction auto-commits without touching disk, so the probe is effectively free.
 */
function isUsable(db: IDBDatabase): boolean {
  try {
    db.transaction(STORE, "readonly");
    return true;
  } catch {
    return false;
  }
}

function openDB(): Promise<IDBDatabase> {
  if (dbInstance && isUsable(dbInstance)) return Promise.resolve(dbInstance);
  dbInstance = null;
  // Dedupe concurrent opens: the viewer hydrates up to 5 files at once, and each
  // would otherwise start its own connection, leaking every one but the last.
  openPromise ??= new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => {
      openPromise = null;
      reject(request.error);
    };
    request.onsuccess = () => {
      const db = request.result;
      // Step aside so another tab can upgrade, and drop the handle either way:
      // `close` fires only on a forced close, never after an explicit close().
      db.onversionchange = () => {
        db.close();
        forget(db);
      };
      db.onclose = () => forget(db);
      dbInstance = db;
      openPromise = null;
      resolve(db);
    };
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "fileVersionId" });
      }
    };
  });
  return openPromise;
}

function buildCubeUniqueIdMap(data: Record<string, string | number | boolean | null> | null): Record<string, number[]> {
  const cubeUniqueIdMap: Record<string, number[]> = {};
  if (!data) return cubeUniqueIdMap;
  for (const nodeId in data) {
    const mapKey = data[nodeId] as string;
    if (!mapKey) continue;
    (cubeUniqueIdMap[mapKey] ??= []).push(Number(nodeId));
  }
  return cubeUniqueIdMap;
}

export async function setNodeIdAndUniqueIdMapForFile(
  fileVersionId: string,
  data: Record<string, string | number | boolean | null> | null,
): Promise<void> {
  const entry: NodeIdUniqueIdFileEntry = {
    nodeIdMap: data,
    cubeUniqueIdMap: buildCubeUniqueIdMap(data),
  };

  memoryCache.set(fileVersionId, entry);

  const row: StoredRow = { fileVersionId, ...entry };
  if (!(await ensureIndexedDbSpace(row))) return;
  const db = await openDB();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(row);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(new Error("IndexedDB transaction aborted", { cause: tx.error }));
  });
}

export async function hasNodeIdAndUniqueIdMap(fileVersionId: string): Promise<boolean> {
  if (memoryCache.has(fileVersionId)) return true;
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const req = db.transaction(STORE, "readonly").objectStore(STORE).getKey(fileVersionId);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result !== undefined);
  });
}

export async function loadNodeIdAndUniqueIdMapIntoCache(fileVersionId: string): Promise<void> {
  if (memoryCache.has(fileVersionId)) return;
  const db = await openDB();
  const row = await new Promise<StoredRow | undefined>((resolve, reject) => {
    const req = db.transaction(STORE, "readonly").objectStore(STORE).get(fileVersionId);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result as StoredRow | undefined);
  });
  if (row) {
    memoryCache.set(fileVersionId, { nodeIdMap: row.nodeIdMap, cubeUniqueIdMap: row.cubeUniqueIdMap });
  }
}

export function getNodeIdAndUniqueIdMapEntry(fileVersionId: string): NodeIdUniqueIdFileEntry | undefined {
  return memoryCache.get(fileVersionId);
}

export function getLoadedNodeIdAndUniqueIdFileVersionIds(): string[] {
  return [...memoryCache.keys()];
}

export function clearOtherNodeIdAndUniqueIdCacheMaps(keepFileVersionIds: string[]): void {
  const keep = new Set(keepFileVersionIds);
  for (const id of memoryCache.keys()) {
    if (!keep.has(id)) memoryCache.delete(id);
  }
}

export async function clearOtherNodeIdAndUniqueIdMaps(keepFileVersionIds: string[]): Promise<void> {
  const keep = new Set(keepFileVersionIds);
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    const objectStore = tx.objectStore(STORE);
    const cursorReq = objectStore.openKeyCursor();
    cursorReq.onsuccess = () => {
      const cursor = cursorReq.result;
      if (!cursor) return;
      if (!keep.has(cursor.key as string)) objectStore.delete(cursor.key);
      cursor.continue();
    };
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(new Error("IndexedDB transaction aborted", { cause: tx.error }));
  });
}
